import { Dispatch, SetStateAction, useMemo, useRef } from "react"

// ** Third Party Components
import { Printer } from "lucide-react"
import { useReactToPrint } from "react-to-print"

// ** Custom Components
import { Button } from "../common/button"
import { Separator } from "../common/separator"
import { Dialog, DialogContent } from "../common/dialog"

// ** Custom hooks
import useTranslator from "@pma/hooks/use-translator"

// ** Helpers
import { cn } from "@pma/utils/helpers"
import { formatCurrency } from "@pma/utils/utils"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../common/table"
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table"
import { DataTableColumnHeader } from "../common/data-table/data-table-column-header"

interface MainBillOfQuantitiesPrintProps {
  isLoading: boolean
  mainBillOfQuantitiesPrintDialog: boolean
  data: any[]
  setMainBillOfQuantitiesPrintDialog: Dispatch<SetStateAction<boolean>>
}

const MainBillOfQuantitiesPrint = ({
  data,
  isLoading,
  mainBillOfQuantitiesPrintDialog,
  setMainBillOfQuantitiesPrintDialog,
}: MainBillOfQuantitiesPrintProps) => {
  const { translate, locale } = useTranslator()
  const billRef = useRef<HTMLDivElement>(null)

  const isHebrew = locale === "he"

  const printBill = useReactToPrint({ contentRef: billRef, documentTitle: "ddd" })

  const columns = useMemo((): ColumnDef<any>[] => {
    const baseColumns: ColumnDef<any>[] = [
      {
        accessorKey: "number",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Section')} className='' />
        ),
        cell: ({ row }: any) => {
          const itemNumber = row?.original?.number
          return (
            <div className='flex gap-1'>
              <span className='text-xs sm:text-sm'>{itemNumber}</span>
            </div>
          )
        },
        enableSorting: false,
        size: 100,
      },
      {
        accessorKey: "description",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Description')} className='' />
        ),
        cell: ({ row }: any) => {
          const itemDescription = row?.original?.description
          return (
            <div className='max-w-[200px] text-right text-xs leading-tight sm:max-w-xs sm:text-sm lg:min-w-[370px]'>
              {itemDescription}
            </div>
          )
        },
        size: 300,
        enableSorting: false,
      },
      {
        accessorKey: "units",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Unit')} className='text-center' />
        ),
        cell: ({ row }: any) => {
          const itemUnit = row?.original?.units
          return <div className='text-center text-xs sm:text-sm'>{itemUnit}</div>
        },
        enableSorting: false,
      },
      {
        accessorKey: "boq_cost",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Price per unit')} className='' />
        ),
        cell: ({ row }: any) => {
          const item = row?.original
          return (
            <div className='min-w-fit text-center text-xs sm:text-sm'>
              <span className='flex items-center gap-1'>
                {formatCurrency(item?.discount_price, isHebrew)}
                <small className='text-[10px]'>
                  {" "}
                  ({Number(((item?.total_discount ?? 0) * 100).toFixed(2))}%)
                </small>{" "}
              </span>{" "}
            </div>
          )
        },
        size: 150,
        enableSorting: false,
      },
      {
        accessorKey: "amount",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Amount from estimate')} className='' />
        ),
        cell: ({ row }: any) => {
          return (
            <div className='max-w-xs text-right text-xs leading-tight sm:text-sm'>
              {row?.original?.amount || ""}
            </div>
          )
        },
        size: 100,
        enableSorting: false,
      },
      {
        accessorKey: "paid",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Accumulated Amount')} className='' />
        ),
        cell: ({ row }: any) => {
          const item = row?.original
          return <div className='text-center text-xs sm:text-sm'>{item?.paid}</div>
        },
        size: 50,
        enableSorting: false,
      },
      {
        accessorKey: "estimated_sum",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Boq sum')} className='' />
        ),
        cell: ({ row }: any) => {
          const item = row?.original
          return (
            <div className='text-center text-xs font-bold sm:text-sm'>
              <span className=''>{formatCurrency(item?.real_sum, isHebrew)}</span>
            </div>
          )
        },
        enableSorting: false,
      },
      {
        accessorKey: "sum",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Accumulated Sum')} className='' />
        ),
        cell: ({ row }: any) => {
          const item = row?.original
          return (
            <div className='flex flex-col items-center'>
              <span>
                {formatCurrency(item?.paid_sum, isHebrew)}
                {/* {formatCurrency(item?.discount_price * item?.paid, isHebrew)} */}
                </span>
            </div>
          )
        },
        enableSorting: false,
      },
    ]
    return baseColumns
  }, [isHebrew])

  const table = useReactTable({ data, columns, getCoreRowModel: getCoreRowModel() })

  return (
    <Dialog
      open={mainBillOfQuantitiesPrintDialog}
      onOpenChange={setMainBillOfQuantitiesPrintDialog}
    >
      <DialogContent
        className={cn(
          "flex h-full max-h-[95vh] w-full flex-col overflow-y-hidden md:max-w-2xl lg:max-w-3xl xl:max-w-7xl",
          isHebrew ? "rtl" : "ltr",
        )}
      >
        <Button
          variant={"default"}
          onClick={printBill}
          className='h-8 w-[100px]'
          disabled={isLoading}
        >
          <Printer className='mx-1 h-4 w-4' />
          {translate("Print")}
        </Button>
        <Separator />

        <style>
          {`
          @media print {
            @page {
              size: landscape;
            }
          }
        `}
        </style>
        <div className='flex-1 overflow-y-auto' ref={billRef}>
          <div className='w-full bg-gray-50 px-2 py-4 sm:px-4 md:py-8'>
            <div
              id={`ddd`}
              className='w-full rounded-lg bg-white p-4 shadow-lg sm:p-6 md:p-8 print:p-0 print:shadow-none'
              dir={isHebrew ? "rtl" : "ltr"}
            >
              <div className='mb-6 flex flex-col items-start justify-between gap-4 border-b-2 border-gray-200 pb-4 sm:flex-row'>
                <div className='flex w-full items-center gap-3 sm:w-auto'>
                  <div className='min-w-0'>
                    <h1 className='mb-1 text-xl font-bold text-blue-600 sm:text-2xl md:text-3xl'>
                      {translate("Bill of Quantities")}
                    </h1>
                  </div>
                </div>
              </div>
              <div className='overflow-x-auto rounded-md border'>
                <Table className='text-xs sm:text-sm'>
                  <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id} className='border-b bg-gray-50'>
                        {headerGroup.headers.map((header) => (
                          <TableHead
                            key={header.id}
                            className={cn(
                              "border-gray-200 px-1 py-2 text-center text-gray-900 sm:px-2 sm:py-3",
                              isHebrew ? "border-r first:border-r-0" : "border-l first:border-l-0",
                            )}
                            style={{ width: header.getSize() }}
                          >
                            {header.isPlaceholder ? null : (
                              <div
                                className={
                                  header.column.getCanSort()
                                    ? "flex cursor-pointer select-none items-center justify-center gap-1"
                                    : "flex items-center justify-center"
                                }
                                onClick={header.column.getToggleSortingHandler()}
                              >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                              </div>
                            )}
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {table.getRowModel().rows.map((row, index) => {
                      const rowData = row.original
                      const isParent = rowData.hasChildren
                      return (
                        <TableRow
                          key={row.id}
                          className={cn(
                            `border-b hover:bg-gray-50 ${
                              index % 2 === 0 ? "bg-white" : "bg-gray-25"
                            } ${isParent ? "font-medium" : ""}`,
                            rowData?.bill?.amount && "border-y border-primary bg-primary/5",
                          )}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <TableCell
                              key={cell.id}
                              className={cn(
                                "border-gray-200 px-1 py-1.5 sm:px-2 sm:py-2",
                                isHebrew
                                  ? "border-r first:border-r-0"
                                  : "border-l first:border-l-0",
                              )}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                          ))}
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default MainBillOfQuantitiesPrint
